import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Contact as ContactIcon, Pencil, Trash2, Phone, Mail, Globe } from "lucide-react";
import api from "@/lib/api";
import { CONTACT_TYPES, CONTACT_TYPE_META } from "@/lib/contacts";
import LoadingScreen from "@/components/LoadingScreen";
import ContactFormModal from "@/components/ContactFormModal";
import { Button } from "@/components/ui/button";
import ListSearch from "@/components/ListSearch";
import Highlight from "@/components/Highlight";
import { matchesQuery, buildHaystack } from "@/lib/search";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function TypeBadge({ type }) {
  const meta = CONTACT_TYPE_META[type] || {};
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`font-normal gap-1.5 ${meta.className || ""}`}>
      {Icon && <Icon className="h-3 w-3" />} {type}
    </Badge>
  );
}

export default function Contacts() {
  const [contacts, setContacts] = useState(null);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [typeFilter, setTypeFilter] = useState("todos");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => toast.error("No se pudieron cargar los contactos."));
  }, []);

  const filtered = useMemo(() => {
    if (!contacts) return [];
    return contacts.filter((c) =>
      (typeFilter === "todos" || c.type === typeFilter) &&
      matchesQuery(search, buildHaystack(c))
    );
  }, [contacts, search, typeFilter]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setModalOpen(true); };

  const onSaved = (saved, wasEditing) => {
    setContacts((list) => {
      const next = wasEditing ? list.map((c) => (c.contact_id === saved.contact_id ? saved : c)) : [...list, saved];
      return [...next].sort((a, b) => a.company.localeCompare(b.company, "es"));
    });
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/contacts/${toDelete.contact_id}`);
      setContacts((list) => list.filter((c) => c.contact_id !== toDelete.contact_id));
      toast.success("Contacto eliminado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    } finally {
      setToDelete(null);
    }
  };

  if (!contacts) return <LoadingScreen text="Cargando contactos…" />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="caption mb-2">CRM · Contactos</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Contactos</h1>
          <p className="text-muted-foreground mt-2">Proveedores y suministradores que podrás asignar a tus recursos.</p>
        </div>
        <Button className="rounded-full gap-2 h-11 px-5" onClick={openNew} data-testid="new-contact-button">
          <Plus className="h-4 w-4" /> Nuevo contacto
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <ListSearch className="flex-1" value={search} onChange={setSearch}
          placeholder="Buscar por cualquier contenido…" testId="contact-search-input" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="contact-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {CONTACT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {contacts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-14 text-center" data-testid="contacts-empty-state">
          <div className="mx-auto h-12 w-12 rounded-xl bg-accent text-olive flex items-center justify-center mb-4">
            <ContactIcon className="h-5 w-5" />
          </div>
          <h2 className="text-xl font-semibold">Aún no hay contactos</h2>
          <p className="text-sm text-muted-foreground mt-2 mb-5">Añade tus proveedores y suministradores al CRM.</p>
          <Button className="rounded-full gap-2" onClick={openNew} data-testid="empty-new-contact-button">
            <Plus className="h-4 w-4" /> Nuevo contacto
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Contacto</TableHead>
                  <TableHead>Relación</TableHead>
                  <TableHead className="hidden xl:table-cell">Actividad</TableHead>
                  <TableHead className="hidden md:table-cell">Persona</TableHead>
                  <TableHead className="hidden lg:table-cell">Teléfono / Email</TableHead>
                  <TableHead className="hidden xl:table-cell">Población</TableHead>
                  <TableHead className="text-right w-[1%]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c, i) => (
                  <TableRow key={c.contact_id} data-testid={`contact-row-${i}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span><Highlight text={c.company} query={search} /></span>
                        <Badge variant="outline" className="font-normal text-[10px] px-1.5 py-0 border-[#C75B28]/25 text-[#C75B28]">
                          {c.entity_type === "Persona" ? "Persona" : "Empresa"}
                        </Badge>
                      </div>
                      {c.cif_nif && <div className="text-[11px] text-muted-foreground font-mono mt-0.5" data-testid={`contact-cif-${i}`}>{c.cif_nif}</div>}
                      {c.website && (
                        <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline mt-0.5">
                          <Globe className="h-3 w-3" /> Web
                        </a>
                      )}
                    </TableCell>
                    <TableCell><TypeBadge type={c.type} /></TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {(c.activities && c.activities.length) ? (
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {c.activities.slice(0, 2).map((a) => (
                            <Badge key={a} variant="outline" className="font-normal text-[11px]">{a}</Badge>
                          ))}
                          {c.activities.length > 2 && (
                            <Badge variant="outline" className="font-normal text-[11px]">+{c.activities.length - 2}</Badge>
                          )}
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{c.contact_person ? <Highlight text={c.contact_person} query={search} /> : "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                      <div className="flex flex-col gap-0.5 text-xs">
                        {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                        {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                        {!c.phone && !c.email && "—"}
                      </div>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-muted-foreground">{c.location || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-contact-${i}`} aria-label="Editar">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setToDelete(c)} data-testid={`delete-contact-${i}`} aria-label="Eliminar">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-10" data-testid="contacts-no-match">
                      Ningún contacto coincide con el filtro.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ContactFormModal open={modalOpen} onOpenChange={setModalOpen} onSaved={onSaved} contact={editing} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(v) => { if (!v) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{toDelete?.company}»?</AlertDialogTitle>
            <AlertDialogDescription>Se quitará del CRM. Los recursos que lo usaban quedarán sin proveedor asignado.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-contact-button">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
